import { cn } from '@heroui/react';
import {
  useQueryState,
  parseAsString,
  parseAsStringLiteral,
  parseAsJson
} from 'nuqs';
import React, { forwardRef, useEffect } from 'react';

import { ContentHeaderContext } from './ContentHeaderContext';
import {
  layoutValues,
  sortValues,
  type LayoutType,
  type SortType,
  type ActiveOptionsType
} from './types';

const CONTENT_HEADER_NAME = 'ContentHeader';

/** nuqs key used for the header search input (legacy Monitor overview uses `pipelineName`). */
export type ContentHeaderSearchQueryParam = 'search' | 'pipelineName';

type ContentHeaderElement = React.ElementRef<'div'>;
type ContentHeaderProps = React.ComponentPropsWithoutRef<'div'> & {
  searchQueryParam?: ContentHeaderSearchQueryParam;
  onLayoutChange?: (
    layout: LayoutType,
    activeOptions: ActiveOptionsType
  ) => void;
  onFilterChange?: (
    filters: string[],
    filterKey: string,
    activeOptions: ActiveOptionsType
  ) => void;
  onSortChange?: (sort: SortType, activeOptions: ActiveOptionsType) => void;
  onInputChange?: (value: string, activeOptions: ActiveOptionsType) => void;
  searchCallback?: (activeOptions: ActiveOptionsType) => void;
  shouldCallInitialSearch?: boolean;
  isLoaded?: boolean;
  clearOnChangeParams?: unknown[];
};

export const ContentHeader = forwardRef<
  ContentHeaderElement,
  ContentHeaderProps
>((props: ContentHeaderProps, forwardedRef) => {
  const {
    onLayoutChange,
    onSortChange,
    onInputChange,
    searchCallback,
    className,
    children,
    isLoaded = true,
    shouldCallInitialSearch,
    clearOnChangeParams,
    searchQueryParam = 'search',
    ...headerProps
  } = props;
  const [sort, setSort] = useQueryState(
    'sort',
    parseAsStringLiteral(sortValues).withDefault('name')
  );
  const [layout, setLayout] = useQueryState(
    'layout',
    parseAsStringLiteral(layoutValues).withDefault('grid')
  );
  const [searchText, setSearchText] = useQueryState(
    searchQueryParam,
    parseAsString.withDefault('')
  );
  const [filters, setFilters] = useQueryState(
    'filters',
    parseAsJson<Record<string, string[]>>(
      v => v as Record<string, string[]>
    ).withDefault({})
  );

  const activeOptions = {
    sort,
    layout,
    searchText,
    filters
  };

  useEffect(() => {
    if (shouldCallInitialSearch && searchCallback) {
      searchCallback(activeOptions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!clearOnChangeParams || clearOnChangeParams.length === 0) return;
    if (clearOnChangeParams.some(param => param === undefined)) return;

    setSearchText('');
    setFilters({});
    setSort('name');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, clearOnChangeParams);

  const handleInputChange = (value: string) => {
    setSearchText(value);
    onInputChange?.(value, { ...activeOptions, searchText: value });
  };

  const handleSortChange = (newSort: SortType) => {
    setSort(newSort);
    onSortChange?.(newSort, { ...activeOptions, sort: newSort });
  };

  const handleSelectFilter = (filterKey: string, selected: string[]) => {
    if (selected.length === 0) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [filterKey]: _, ...newFilters } = filters;
      setFilters(newFilters);
    } else {
      setFilters(prev => ({
        ...prev,
        [filterKey]: selected
      }));
    }
  };

  const handleLayoutChange = (newLayout: LayoutType) => {
    setLayout(newLayout);
    onLayoutChange?.(newLayout, { ...activeOptions, layout: newLayout });
  };

  const contextValue = {
    layout,
    onLayoutChange: handleLayoutChange,
    handleSelectFilter,
    sort,
    onSortChange: handleSortChange,
    searchText,
    onInputChange: handleInputChange,
    searchCallback,
    activeOptions,
    isLoaded
  };

  return (
    <ContentHeaderContext.Provider value={contextValue}>
      <header
        ref={forwardedRef}
        className={cn('flex items-center justify-between', className)}
        {...headerProps}
      >
        {children}
      </header>
    </ContentHeaderContext.Provider>
  );
});

ContentHeader.displayName = CONTENT_HEADER_NAME;
